// @ts-ignore
import _ from 'lodash'

const TILE_SIZE = 256

// Web Mercator world coordinates in pixels at given zoom — same projection Google Maps uses
export const toWorldPixels = (lat: number, lng: number, zoom: number): { x: number, y: number } => {
    const scale = TILE_SIZE * Math.pow(2, zoom)
    const siny  = _.clamp(Math.sin(lat * Math.PI / 180), -0.9999, 0.9999)

    return {
        x   : (lng + 180) / 360 * scale,
        y   : (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)) * scale
    }
}

// zoom 0 projection per model; pixel distance at any zoom is this distance * 2^zoom, so trig runs once per model, not per zoom
const baseProjection = new WeakMap<object, { x: number, y: number }>()

const getBasePixels = (item: any): { x: number, y: number } => {
    let pixels = baseProjection.get(item)
    if (!pixels) {
        pixels = toWorldPixels(item.lat, item.lng, 0)
        baseProjection.set(item, pixels)
    }
    return pixels
}

// Greedy grouping: each not yet grouped item collects every not yet grouped item within distancePx of it.
// Grid with cell = distance means all candidates are in the 3x3 neighbour cells, so it's ~O(n) instead of O(n²).
export const groupClosePoints = (items: any[], zoom: number, distancePx: number): any[][] => {
    const distance  = distancePx / Math.pow(2, zoom)
    const pixels    = _.map(items, getBasePixels)
    const grid      = new Map<string, number[]>()

    const cellOf = (index: number): number[] => {
        return [
            Math.floor(pixels[index].x / distance),
            Math.floor(pixels[index].y / distance)
        ]
    }

    _.forEach(items, (item: any, index: number) => {
        const cell  = cellOf(index)
        const key   = cell[0] + ':' + cell[1]
        const list  = grid.get(key)
        if (list) {
            list.push(index)
        } else {
            grid.set(key, [index])
        }
        return
    })

    const assigned          = _.fill(new Array(items.length), false)
    const groups: any[][]   = []

    for (let i = 0; i < items.length; i++) {
        if (assigned[i]) {
            continue
        }

        assigned[i] = true
        const members   = [i]
        const cell      = cellOf(i)

        for (let dx = -1; dx <= 1; dx++) {
            for (let dy = -1; dy <= 1; dy++) {
                _.forEach(grid.get((cell[0] + dx) + ':' + (cell[1] + dy)), (j: number) => {
                    if (
                        !assigned[j] &&
                        Math.hypot(pixels[i].x - pixels[j].x, pixels[i].y - pixels[j].y) <= distance
                    ) {
                        assigned[j] = true
                        members.push(j)
                    }
                    return
                })
            }
        }

        // keep input order inside group, as plain scan would
        groups.push(_.map(_.sortBy(members), (index: number) => {
            return items[index]
        }))
    }

    return groups
}
